import type { Observable } from 'rxjs';
import { from, iif, map, mergeMap, of, retry, tap } from 'rxjs';

import { getDomainMarketOrigin } from '@libs/search-service-smart-search';
import type {
  AccommodationType,
  AirportStaticInfo,
  Coordinate,
  DestinationAutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
  GetHotelsResponseMap,
  PortStaticInfo,
  SeaportStaticInfo,
  StaticInfoResponse,
  StaticInfoType,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import { CategoryType } from '@libs/trip-planner-interfaces';
import {
  assertValue,
  isDefined,
  popularityAirportComparer,
  renderQuery,
} from '@libs/utils';
import type { QueryString } from '@libs/utils';
import {
  isExperimentAsync,
  trackFallbackStaticInfoReceived,
} from '@libs/feature-toggle';
import type { HotelConstraint } from '@libs/constraint-builder';
import { isStenaLineAffiliateInURL } from '@libs/cookie';

import { i18n } from '../../localization/localizationSetup';
import { ApiQuery } from '../shared/apiQuery';
import { camelCased } from '../shared/apiQuery.helper';

import type {
  ContentApiFerryRouteStaticData,
  ContentApiFerrySuggestionRequestData,
  ContentApiPlaceStaticData,
  ContentApiPortStaticData,
  GeoJsonLocationItem,
} from './contentApiResponses';
import {
  getHotelsUrlBuilder,
  getHotelsUrlParamsQueryString,
} from './getHotelsUrlBuilder';
import { GET_HOTELS_ENDPOINT_PROPERTIES } from './hotelStaticProperties';

const { t } = i18n.global;

const HOTEL_IMAGES_PROPERTY = 'Hotels.Images.ImageName';

type AutoSuggestionType =
  'Harbour' | 'Airport' | 'AirportForDestination' | 'FerryRoute';

interface BaseAutoSuggestionOptions {
  type: AutoSuggestionType;
  size: number;
}

interface SearchTermAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  searchTerm: string;
}

interface PositionAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  position: Coordinate;
}

interface IataAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  iata: string;
}

interface HarbourAutoSuggestionOptions extends BaseAutoSuggestionOptions {
  harbour: string;
}

interface FerryRouteSuggestionOptions extends BaseAutoSuggestionOptions {
  termFrom?: string;
  termTo?: string;
  latitudeFrom?: number;
  longitudeFrom?: number;
  latitudeTo?: number;
  longitudeTo?: number;
  stenaLineOnlyRoutes?: boolean;
}

type AutoSuggestionOptions =
  | SearchTermAutoSuggestionOptions
  | PositionAutoSuggestionOptions
  | IataAutoSuggestionOptions
  | HarbourAutoSuggestionOptions
  | FerryRouteSuggestionOptions;

export class ContentApi {
  private query: ApiQuery;
  private readonly baseUrl = 'https://content-hc.sembo.com/';
  private trackedFallbackConstraintSelections = new Set<string>();

  public constructor() {
    this.query = new ApiQuery(this.baseUrl);
  }

  public getHotels<T extends StaticInfoType>(
    constraint: HotelConstraint,
    hotelTypes: AccommodationType[],
    type: T
  ): Observable<StaticInfoResponse<T>> {
    const includedProperties = GET_HOTELS_ENDPOINT_PROPERTIES[type];
    const hasImages = includedProperties.includeValues.includes(
      HOTEL_IMAGES_PROPERTY
    );

    // Wait for the experiment so the image order matches the user's group
    const imageOrdering$ = hasImages
      ? from(isExperimentAsync('hotel_images_ordering'))
      : of(false);

    return imageOrdering$.pipe(
      mergeMap((imageOrdering) =>
        this.fetchHotels(
          getHotelsUrlBuilder(
            constraint,
            hotelTypes,
            includedProperties,
            imageOrdering
          ),
          constraint,
          hotelTypes,
          type
        )
      )
    );
  }

  public searchSuggestions(
    searchTerm: string
  ): Observable<DestinationAutoCompleteSuggestion[]> {
    const url = `CategorizedAutosuggestion?Term=${searchTerm}`;
    return this.query
      .getObservable<DestinationAutoCompleteSuggestion[]>(
        url,
        this.contentApiHeaders()
      )
      .pipe(camelCased({ deep: true }));
  }

  public searchAirportSuggestions(
    searchTerm: string
  ): Observable<FlightAutoCompleteSuggestion[]> {
    const options: AutoSuggestionOptions = {
      searchTerm,
      type: 'Airport',
      size: 20,
    };

    return this.searchPortStaticData(options).pipe(
      map((data: ContentApiPortStaticData[]): FlightAutoCompleteSuggestion[] =>
        this.mapToAutoCompleteSuggestion(data, t('planner.airport'))
      )
    );
  }

  public searchAirportSuggestionsByPosition(
    position: Coordinate
  ): Observable<FlightAutoCompleteSuggestion[]> {
    const options: AutoSuggestionOptions = {
      position,
      type: 'Airport',
      size: 20,
    };

    return this.searchPortStaticData(options).pipe(
      map(this.getTopPopularAirports),
      map((data) =>
        this.mapToAutoCompleteSuggestion(
          data,
          t('planner.transport.nearby_airports')
        )
      )
    );
  }

  public searchIataStaticInfo(iata: string): Observable<PortStaticInfo> {
    return this.searchPortStaticData({
      iata,
      type: 'Airport',
      size: 1,
      stenaLineOnlyRoutes: isStenaLineAffiliateInURL(),
    }).pipe(
      map((data: ContentApiPortStaticData[]): PortStaticInfo => {
        const firstDataAirport = data[0]?.airport;
        assertValue(
          firstDataAirport,
          'contentApi.ts > searchIataStaticInfo > firstDataAirport'
        );

        return {
          code: firstDataAirport.code,
          name: firstDataAirport.label,
          countryCode: firstDataAirport.countryCode ?? undefined,
          position: {
            latitude: firstDataAirport.latitude,
            longitude: firstDataAirport.longitude,
            name: firstDataAirport.city ?? undefined,
          },
        };
      })
    );
  }

  public searchSeaportStaticInfo(
    harbour: string,
    size: number
  ): Observable<SeaportStaticInfo[]> {
    return this.searchPortStaticData({
      harbour,
      type: 'Harbour',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): SeaportStaticInfo[] =>
        data.map(this.mapToSeaportStaticInfo)
      )
    );
  }

  public searchFerryRouteStaticInfo(
    harbourFrom: string,
    harbourTo: string
  ): Observable<ContentApiFerryRouteStaticData | null> {
    const queryString: QueryString = {
      Types: ['FerryRoute'],
      HarbourFrom: [harbourFrom],
      HarbourTo: [harbourTo],
    };

    return this.query
      .getObservable<ContentApiPortStaticData[]>(
        this.getAutosuggestionUrl(queryString),
        this.contentApiHeaders()
      )
      .pipe(
        camelCased({ deep: true }),
        map((response) => response[0]?.ferryRoute || null)
      );
  }

  public searchAirportStaticInfo(
    searchTerm: string,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({
      searchTerm,
      type: 'Airport',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public searchFerryRoutes(
    searchTerm: string,
    size: number,
    isStenaLineAffiliateInURL: boolean
  ): Observable<ContentApiFerryRouteStaticData[]> {
    return this.searchPortStaticData({
      type: 'FerryRoute',
      searchTerm,
      size,
      stenaLineOnlyRoutes: isStenaLineAffiliateInURL,
    }).pipe(
      map(
        (data: ContentApiPortStaticData[]): ContentApiFerryRouteStaticData[] =>
          data.map(this.mapToFerryRoutes)
      )
    );
  }

  public getFerryRoutesSuggestions(
    from: ContentApiFerrySuggestionRequestData,
    to: ContentApiFerrySuggestionRequestData,
    isStenaLine: boolean
  ): Observable<ContentApiFerryRouteStaticData[]> {
    const MAX_FERRY_ROUTES = 5;

    const options: AutoSuggestionOptions = {
      type: 'FerryRoute',
      size: MAX_FERRY_ROUTES,
      termTo: to.location,
      latitudeFrom: from.lat,
      longitudeFrom: from.long,
      latitudeTo: to.lat,
      longitudeTo: to.long,
      stenaLineOnlyRoutes: isStenaLine,
    };

    if (from.location !== to.location) {
      options.termFrom = from.location;
    }

    return this.searchPortStaticData(options).pipe(
      map(
        (data: ContentApiPortStaticData[]): ContentApiFerryRouteStaticData[] =>
          data.map(this.mapToFerryRoutes)
      )
    );
  }

  public searchAirportStaticInfoForDestination(
    searchTerm: string,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({
      searchTerm,
      type: 'AirportForDestination',
      size,
    }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public searchAirportStaticInfoForPosition(
    position: Coordinate,
    size: number
  ): Observable<AirportStaticInfo[]> {
    return this.searchPortStaticData({ position, type: 'Airport', size }).pipe(
      map((data: ContentApiPortStaticData[]): AirportStaticInfo[] =>
        data.map(this.mapToAirportStaticInfo)
      )
    );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    const url = '/VehicleTypes';

    return this.query
      .getObservable<TransportVehicle[]>(url, this.contentApiHeaders())
      .pipe(camelCased({ deep: true }));
  }

  public fetchGeoJsonData(
    polygonIds: number[]
  ): Observable<GeoJsonLocationItem[]> {
    const contentQuery = new ApiQuery('https://content.sembo.com/');
    return contentQuery
      .postObservable<number[], GeoJsonLocationItem[]>(
        'Polygons/GeoJsonData',
        polygonIds,
        this.contentApiHeaders()
      )
      .pipe(camelCased({ deep: true }));
  }

  // Fallback country lookup for destinations that don't carry a reliable
  // country of their own: own stays never have static address data, and
  // hotels occasionally have unrecognized/missing address country values.
  // Callers must pass a destination-level polygon id (city/region/country) —
  // a hotel's own mainPolygon id is always neighborhood-level and has no
  // corresponding Place content page.
  public fetchPlaceCountryCode(
    polygonId: number
  ): Observable<string | undefined> {
    const contentQuery = new ApiQuery('https://content.sembo.com/');
    return contentQuery
      .getObservable<ContentApiPlaceStaticData>(
        `Place/PolygonId/${polygonId}`,
        this.contentApiHeaders()
      )
      .pipe(
        camelCased({ deep: true }),
        map((place) => place.isoCountryCode ?? undefined)
      );
  }

  private fetchHotels<T extends StaticInfoType>(
    url: string,
    constraint: HotelConstraint,
    hotelTypes: AccommodationType[],
    type: T
  ): Observable<StaticInfoResponse<T>> {
    return this.query
      .getObservable<GetHotelsResponseMap[T]>(url, this.contentApiHeaders())
      .pipe(
        camelCased({
          deep: true,
        }),
        mergeMap((response) =>
          iif(
            () => shouldTryFallback(constraint, response),
            this.query
              .getObservable<GetHotelsResponseMap[T]>(
                url,
                this.contentApiHeaders({ forceDefaultMarket: true })
              )
              .pipe(
                camelCased({
                  deep: true,
                }),
                tap((response) => {
                  const serializedConstraintSelection = `${constraint.selection.serializeValue()}${hotelTypes.toString()}`;
                  if (
                    response.hotels.length > 0 &&
                    !this.trackedFallbackConstraintSelections.has(
                      serializedConstraintSelection
                    )
                  ) {
                    this.trackedFallbackConstraintSelections.add(
                      serializedConstraintSelection
                    );

                    const queryString = getHotelsUrlParamsQueryString(
                      constraint,
                      hotelTypes
                    );

                    trackFallbackStaticInfoReceived({
                      searchTerm: queryString.SearchText,
                      hotelTypes: queryString.HotelTypes,
                      searchType: type,
                    });
                  }
                })
              ),
            of(response)
          )
        ),
        retry(1),
        map((response) => ({ response, type }))
      );

    function shouldTryFallback(
      constraint: HotelConstraint,
      response: GetHotelsResponseMap[T]
    ): boolean {
      const { selection, maxNumberOfHotels, ...constraintFilters } = constraint;
      const hasFilters = Object.values(constraintFilters).some((value) =>
        isDefined(value)
      );

      return !hasFilters && response.hotels.length === 0;
    }
  }

  private searchPortStaticData(
    options: AutoSuggestionOptions
  ): Observable<ContentApiPortStaticData[]> {
    const queryString: QueryString = {
      Types: [options.type],
      Size: [`${options.size}`],
    };

    if ('searchTerm' in options) {
      queryString.Term = [options.searchTerm];
    }

    if ('position' in options) {
      queryString.Latitude = [`${options.position.latitude}`];
      queryString.Longitude = [`${options.position.longitude}`];
    }

    if ('iata' in options) {
      queryString.IataCode = [options.iata];
    }

    if ('harbour' in options) {
      queryString.Harbour = [options.harbour];
    }

    if ('termFrom' in options && options.termFrom) {
      queryString.TermFrom = [options.termFrom];
    }
    if ('termTo' in options && options.termTo) {
      queryString.TermTo = [options.termTo];
    }
    if ('latitudeFrom' in options && options.latitudeFrom) {
      queryString.LatitudeFrom = [options.latitudeFrom.toString()];
    }
    if ('longitudeFrom' in options && options.longitudeFrom) {
      queryString.LongitudeFrom = [options.longitudeFrom.toString()];
    }
    if ('latitudeTo' in options && options.latitudeTo) {
      queryString.LatitudeTo = [options.latitudeTo.toString()];
    }
    if ('longitudeTo' in options && options.longitudeTo) {
      queryString.LongitudeTo = [options.longitudeTo.toString()];
    }

    if ('stenaLineOnlyRoutes' in options && options.stenaLineOnlyRoutes) {
      queryString.StenaLineFerryRoutesOnly = ['true'];
    }

    return this.query
      .getObservable<ContentApiPortStaticData[]>(
        this.getAutosuggestionUrl(queryString),
        this.contentApiHeaders()
      )
      .pipe(camelCased({ deep: true }));
  }

  private getAutosuggestionUrl(query: QueryString): string {
    return `Autosuggestion?${renderQuery(query)}`;
  }

  private contentApiHeaders(options?: {
    forceDefaultMarket?: boolean;
  }): HeadersInit {
    return {
      'X-Sembo-Host': getDomainMarketOrigin(options),
      'X-Sembo-App': 'planner',
    };
  }

  private mapToAirportStaticInfo = (
    port: ContentApiPortStaticData
  ): AirportStaticInfo => {
    const airport = port.airport;
    assertValue(airport, 'contentApi.ts > mapToAirportStaticInfo > airport');

    return {
      type: 'airport',
      label: airport.label,
      code: airport.code,
      city: airport.city ?? undefined,
      latitude: airport.latitude,
      longitude: airport.longitude,
      popularity: airport.popularity,
    };
  };

  private mapToSeaportStaticInfo(
    this: void,
    port: ContentApiPortStaticData
  ): SeaportStaticInfo {
    const harbour = port.harbour;
    assertValue(harbour, 'contentApi.ts > mapToSeaportStaticInfo > harbour');

    return {
      type: 'harbour',
      address: harbour.address,
      code: harbour.code,
      label: harbour.label,
      latitude: harbour.latitude,
      longitude: harbour.longitude,
    };
  }

  private mapToFerryRoutes = (
    data: ContentApiPortStaticData
  ): ContentApiFerryRouteStaticData => {
    assertValue(
      data.ferryRoute,
      'contentApi.ts > mapToFerryRoutes > ferryRoute'
    );
    return data.ferryRoute;
  };

  private getTopPopularAirports = (
    data: ContentApiPortStaticData[]
  ): ContentApiPortStaticData[] => {
    const POSITION_SUGGESTION_COUNT = 10;

    const topPopularAirports = [...data]
      .sort(popularityAirportComparer)
      .splice(0, POSITION_SUGGESTION_COUNT);

    return data.filter(
      (airport) =>
        !!topPopularAirports.find(
          (popularAirport) =>
            popularAirport.airport?.code === airport.airport?.code
        )
    );
  };

  private mapToAutoCompleteSuggestion(
    data: ContentApiPortStaticData[],
    category: string
  ): FlightAutoCompleteSuggestion[] {
    return data.map((portStaticData) => {
      const airport = portStaticData.airport;
      assertValue(
        airport,
        'contentApi.ts > mapToAutoCompleteSuggestion > airport'
      );

      return {
        iata: airport.code,
        categoryType: CategoryType.Airport,
        label: airport.label,
        tooltip: airport.city ?? undefined,
        category,
      };
    });
  }
}
